import React, { useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { FiPlay, FiPause, FiVolume2 } from "react-icons/fi";

const SPEEDS = [1, 1.5, 2];

const InlineAudioPlayer = forwardRef(({ src, onTimeUpdate, onPause }, ref) => {
  const audioEl = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);
  const progressRef = useRef(null);

  useImperativeHandle(ref, () => ({
    seekTo(time) {
      if (audioEl.current) {
        audioEl.current.currentTime = time;
        audioEl.current.play().catch(() => {});
        setPlaying(true);
      }
    },
    getCurrentTime() {
      return currentTime;
    },
  }));

  const togglePlay = useCallback(
    (e) => {
      e.stopPropagation();
      if (!audioEl.current) return;
      if (playing) {
        audioEl.current.pause();
      } else {
        audioEl.current.play().catch(() => {});
      }
    },
    [playing]
  );

  const cycleSpeed = useCallback(
    (e) => {
      e.stopPropagation();
      const nextIdx = (speedIdx + 1) % SPEEDS.length;
      setSpeedIdx(nextIdx);
      if (audioEl.current) {
        audioEl.current.playbackRate = SPEEDS[nextIdx];
      }
    },
    [speedIdx]
  );

  const handleProgressClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!progressRef.current || !audioEl.current || !duration) return;
      const rect = progressRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audioEl.current.currentTime = ratio * duration;
    },
    [duration]
  );

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="flex items-center gap-3 px-3.5 py-2.5 bg-white rounded-lg"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={() => {}}
    >
      <audio
        ref={audioEl}
        preload="metadata"
        onTimeUpdate={(e) => {
          const t = e.target.currentTime;
          setCurrentTime(t);
          onTimeUpdate?.(t);
        }}
        onPause={(e) => {
          setPlaying(false);
          onPause?.(e.target.currentTime);
        }}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onPlay={() => setPlaying(true)}
      >
        <source src={src} />
        <track kind="captions" />
      </audio>

      {/* Play/Pause */}
      <button
        type="button"
        onClick={togglePlay}
        className="shrink-0 w-5 h-5 flex items-center justify-center text-gray-900 hover:text-primary transition-colors"
      >
        {playing ? <FiPause size={16} /> : <FiPlay size={16} />}
      </button>

      {/* Speed */}
      <button
        type="button"
        onClick={cycleSpeed}
        className="text-sm text-gray-900 shrink-0 hover:text-primary transition-colors"
      >
        x{SPEEDS[speedIdx].toString().replace(".", ",")}
      </button>

      {/* Time */}
      <span className="text-sm text-gray-900 shrink-0 tabular-nums">{formatTime(currentTime)}</span>

      {/* Progress bar */}
      <div
        ref={progressRef}
        className="flex-1 h-1 bg-gray-300 rounded-[200px] cursor-pointer relative overflow-hidden min-w-[40px]"
        onClick={handleProgressClick}
        onKeyDown={() => {}}
      >
        <div
          className="h-full bg-gradient-to-r from-pink-500 to-indigo-600 rounded-[200px]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Volume icon */}
      <FiVolume2
        size={16}
        className="text-gray-400 shrink-0"
      />
    </div>
  );
});

export default InlineAudioPlayer;
